import React, { useMemo, useState } from "react";
import { BadgeCheck, MapPin, Quote, Star, Plus, Search } from "lucide-react";
import {
  FEATURED_REVIEWS,
  REVIEW_BADGES,
  REVIEW_COUNTRIES,
  TESTIMONIALS,
} from "../../data/testimonials";
import { Reveal, SectionHeading } from "../common";

const PAGE = 9;

function Stars() {
  return (
    <span className="flex gap-0.5">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} className="h-3.5 w-3.5 fill-ivi-green text-ivi-green" />
      ))}
    </span>
  );
}

export default function Testimonials() {
  const [country, setCountry] = useState("All");
  const [query, setQuery] = useState("");
  const [shown, setShown] = useState(PAGE);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TESTIMONIALS.filter(
      (t) =>
        (country === "All" || t.country === country) &&
        (!q ||
          t.quote.toLowerCase().includes(q) ||
          t.program.toLowerCase().includes(q) ||
          t.country.toLowerCase().includes(q) ||
          (t.name || "").toLowerCase().includes(q))
    );
  }, [country, query]);

  const visible = filtered.slice(0, shown);

  const choose = (c) => {
    setCountry(c);
    setShown(PAGE);
  };

  return (
    <section id="reviews" className="relative overflow-hidden bg-ivi-sand py-24 lg:py-32">
      <div className="pointer-events-none absolute -left-32 top-24 h-80 w-80 rounded-full bg-white/60 blur-3xl" />
      <div className="relative mx-auto max-w-[1400px] px-5 lg:px-8">
        <SectionHeading
          eyebrow="Volunteer reviews"
          title={`${TESTIMONIALS.length + FEATURED_REVIEWS.length} stories from people who went`}
          text="Unedited words from IVI alumni across 6 continents. Filter by destination or search for the project you're considering."
        />

        {/* Trust badges */}
        <Reveal className="mt-10">
          <div className="mx-auto flex max-w-4xl flex-wrap items-stretch justify-center gap-3">
            {REVIEW_BADGES.map((b) => (
              <div
                key={b.source}
                className="flex items-center gap-3 rounded-2xl bg-white px-5 py-4 shadow-[0_10px_28px_-22px_rgba(24,40,32,0.5)]"
              >
                <span className="grid h-11 w-11 place-items-center rounded-xl bg-ivi-mint text-ivi-greenDark">
                  <BadgeCheck className="h-5 w-5" />
                </span>
                <span>
                  <span className="block font-heading text-[17px] font-extrabold leading-none text-ivi-slate">
                    {b.score}
                  </span>
                  <span className="mt-1 block text-[12px] font-semibold text-ivi-slateSoft">
                    {b.source} · {b.detail}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Featured three */}
        <div className="mt-14 grid items-stretch gap-6 lg:grid-cols-3">
          {FEATURED_REVIEWS.map((f, i) => (
            <Reveal key={f.id} delay={i * 90} className="h-full">
              <figure className="group flex h-full flex-col overflow-hidden rounded-[28px] bg-white shadow-[0_20px_50px_-34px_rgba(24,40,32,0.55)]">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={f.img}
                    alt={`${f.name} volunteering in ${f.country}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[1000ms] group-hover:scale-110"
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-ivi-slate/70 to-transparent" />
                  <span className="absolute inset-x-5 bottom-4 flex items-end justify-between gap-3">
                    <span>
                      <span className="block font-heading text-[20px] font-extrabold text-white">
                        {f.name}
                      </span>
                      <span className="mt-0.5 flex items-center gap-1.5 text-[13px] font-semibold text-white/80">
                        <MapPin className="h-3.5 w-3.5" /> {f.country} · {f.program}
                      </span>
                    </span>
                    <Quote className="h-9 w-9 shrink-0 text-white/45" />
                  </span>
                </div>
                <blockquote className="flex flex-1 flex-col p-7">
                  <Stars />
                  <p className="mt-4 flex-1 text-[15px] leading-relaxed text-ivi-slate">
                    &ldquo;{f.quote}&rdquo;
                  </p>
                </blockquote>
              </figure>
            </Reveal>
          ))}
        </div>

        {/* Filters */}
        <div className="mt-16 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {REVIEW_COUNTRIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => choose(c)}
                className={`rounded-full px-4 py-2 text-[13px] font-semibold transition-[background-color,color,border-color,transform] duration-200 hover:-translate-y-0.5 ${
                  country === c
                    ? "bg-ivi-green text-white shadow-[0_10px_20px_-12px_rgba(90,158,47,1)]"
                    : "border border-ivi-mintDeep bg-white text-ivi-slate hover:border-ivi-green"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-80">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ivi-slateSoft" />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShown(PAGE);
              }}
              placeholder="Search turtles, teaching, Fiji..."
              className="h-12 w-full rounded-full border border-ivi-mintDeep bg-white pl-11 pr-4 text-[14px] text-ivi-slate outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-ivi-slateSoft focus:border-ivi-green focus:ring-2 focus:ring-ivi-green/25"
            />
          </div>
        </div>

        <p className="mt-6 font-heading text-[14px] font-bold text-ivi-slate">
          Showing {visible.length} of {filtered.length} reviews
        </p>

        {/* Masonry-ish grid */}
        {filtered.length === 0 ? (
          <div className="mt-8 rounded-3xl border border-dashed border-white bg-white/60 p-16 text-center">
            <p className="font-heading text-[19px] font-bold text-ivi-slate">No reviews match that</p>
            <p className="mt-2 text-[15px] text-ivi-slateSoft">
              Try another destination or clear your search.
            </p>
          </div>
        ) : (
          <div className="mt-8 columns-1 gap-6 sm:columns-2 lg:columns-3 [&>*]:mb-6">
            {visible.map((t, i) => (
              <Reveal key={t.id} delay={(i % 3) * 70} className="break-inside-avoid">
                <figure className="group overflow-hidden rounded-3xl bg-white shadow-[0_14px_38px_-28px_rgba(24,40,32,0.5)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_30px_58px_-30px_rgba(24,40,32,0.5)]">
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={t.img}
                      alt={`${t.program} in ${t.country}`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[1000ms] group-hover:scale-110"
                    />
                    <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white/92 px-3 py-1 font-heading text-[11px] font-bold uppercase tracking-[0.12em] text-ivi-greenDark">
                      <MapPin className="h-3 w-3" /> {t.country}
                    </span>
                  </div>
                  <blockquote className="p-6">
                    <Stars />
                    <p className="mt-3 text-[15px] leading-relaxed text-ivi-slate">
                      &ldquo;{t.quote}&rdquo;
                    </p>
                    <figcaption className="mt-5 border-t border-ivi-mint pt-4">
                      <span className="block font-heading text-[14px] font-extrabold text-ivi-slate">
                        {t.name || "IVI Volunteer"}
                      </span>
                      <span className="mt-0.5 block text-[13px] text-ivi-slateSoft">
                        {t.program} · {t.date}
                      </span>
                    </figcaption>
                  </blockquote>
                </figure>
              </Reveal>
            ))}
          </div>
        )}

        {visible.length < filtered.length && (
          <div className="mt-12 flex justify-center">
            <button
              type="button"
              onClick={() => setShown((s) => s + PAGE)}
              className="group inline-flex items-center gap-2 rounded-full bg-ivi-slate px-8 py-4 font-heading text-[15px] font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-ivi-slate/90"
            >
              <Plus className="h-4 w-4 text-ivi-lime transition-transform duration-200 group-hover:rotate-90" />
              Load {Math.min(PAGE, filtered.length - visible.length)} more reviews
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
